/**
 * Standing pins — pure derivations.
 *
 * What the board needs answered about pins without React or the server:
 * which pin covers a card (worker × work center × day → end date), what the
 * toast says after "keep it longer", the default "until" date, the role a
 * standing pin should carry, and how the standing-crew list is ordered.
 */
import { addDays, format, parseISO } from "date-fns";

import { ExtendResult, RosterAssignment, StandingPin } from "../../../../../services/roster-assignments-api";
import { localDay, weekEndIso } from "../../../utils/time";

/** The date part of whatever the server sent ("2026-10-09" or "2026-10-09T00:00:00"). */
const dayOf = (value: string): string => value.slice(0, 10);

/** "Sun 5 Oct" from a yyyy-MM-dd (or longer ISO) string. */
export const fmtDay = (value: string): string => format(parseISO(dayOf(value)), "EEE d MMM");

const plural = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? "" : "s"}`;

// ---------------------------------------------------------------------------

/**
 * The pin that covers a worker on a work center on a day, if any. Pins are
 * matched on worker + work center + date (inclusive range); when several
 * overlap the one that runs longest wins, so the card reads the full range.
 */
export function findCoveringPin(
	pins: StandingPin[],
	workerId: string,
	workstation: string | null | undefined,
	day: Date | string
): StandingPin | null {
	if (!workstation) {
		return null;
	}
	const date = typeof day === "string" ? dayOf(day) : localDay(day);
	const worker = String(workerId);
	const center = workstation.trim().toLowerCase();
	let best: StandingPin | null = null;
	pins.forEach((pin) => {
		if (String(pin.workerId) !== worker || pin.workstation.trim().toLowerCase() !== center) {
			return;
		}
		if (dayOf(pin.startDate) > date || dayOf(pin.endDate) < date) {
			return;
		}
		if (!best || dayOf(pin.endDate) > dayOf(best.endDate)) {
			best = pin;
		}
	});
	return best;
}

/** yyyy-MM-dd the covering pin ends on, or null when the day is not pinned. */
export function pinnedUntil(
	pins: StandingPin[],
	workerId: string,
	workstation: string | null | undefined,
	day: Date | string
): string | null {
	const pin = findCoveringPin(pins, workerId, workstation, day);
	return pin ? dayOf(pin.endDate) : null;
}

/** "pinned until Fri 9 Oct" — the tooltip / aria fragment a covered card carries. */
export const pinnedUntilLabel = (endDate: string): string => `pinned until ${fmtDay(endDate)}`;

// ---------------------------------------------------------------------------

export interface ExtendToast {
	/** "Nana Gyamfi on Glatt through Sun 5 Oct · 2 days re-pinned · 1 block elsewhere freed, run Allocate to refill" */
	message: string
	/** The server's warnings, joined, for the toast's second line. */
	detail?: string
}

/** Compose the toast for an extend result; zero-count parts are left out. */
export function composeExtendToast(
	result: ExtendResult,
	workerName: string,
	workstation?: string | null
): ExtendToast {
	const parts = [`${workerName} on ${workstation || "the line"} through ${fmtDay(result.endDate)}`];
	if (result.pinnedDays > 0) {
		parts.push(`${plural(result.pinnedDays, "day")} re-pinned`);
	}
	if (result.displacedBlocks > 0) {
		parts.push(`${plural(result.displacedBlocks, "block")} elsewhere freed, run Allocate to refill`);
	}
	if (result.pinsShortened > 0) {
		parts.push(`${plural(result.pinsShortened, "earlier pin")} shortened`);
	}
	const warnings = (result.warnings ?? []).filter(Boolean);
	return { message: parts.join(" · "), detail: warnings.length ? warnings.join(" ") : undefined };
}

// ---------------------------------------------------------------------------

/**
 * Default for "Until…": the Sunday that closes the visible week (weekStart is
 * its Monday) — the same end every other "for the week" range uses. It used
 * to be the Friday, which disagreed with "Keep for the week" and a dropped chip.
 */
export const defaultUntilDate = (weekStart: Date): string => weekEndIso(weekStart);

/**
 * "End today" on a pin: the pin runs to today when it started today, to
 * yesterday when it started earlier (so today is freed). Null when the pin
 * has not started yet or already ended — then only Remove makes sense.
 */
export function endTodayDate(pin: StandingPin, today: Date = new Date()): string | null {
	const todayIso = localDay(today);
	const start = dayOf(pin.startDate);
	const end = dayOf(pin.endDate);
	if (start > todayIso || end < todayIso) {
		return null;
	}
	return start === todayIso ? todayIso : localDay(addDays(parseISO(todayIso), -1));
}

/** Work center A→Z, then start date, then worker. */
export function sortPins(pins: StandingPin[]): StandingPin[] {
	return [...pins].sort(
		(a, b) =>
			a.workstation.localeCompare(b.workstation) ||
			dayOf(a.startDate).localeCompare(dayOf(b.startDate)) ||
			a.workerName.localeCompare(b.workerName)
	);
}

// ---------------------------------------------------------------------------

export interface PrimaryRole {
	jobRoleId: number | null
	jobRoleName: string | null
}

/**
 * The role a worker mostly held in the given assignments — what a standing
 * pin dropped onto them carries. Null parts when nothing is known.
 */
export function primaryRole(assignments: RosterAssignment[], workerId: string): PrimaryRole {
	const counts = new Map<string, { role: PrimaryRole; count: number }>();
	assignments.forEach((a) => {
		if (String(a.workerId) !== String(workerId) || !a.jobRoleName) {
			return;
		}
		const key = a.jobRoleId != null ? `#${a.jobRoleId}` : a.jobRoleName;
		const entry = counts.get(key) || { role: { jobRoleId: a.jobRoleId ?? null, jobRoleName: a.jobRoleName }, count: 0 };
		entry.count += 1;
		counts.set(key, entry);
	});
	const top = Array.from(counts.values()).sort((a, b) => b.count - a.count)[0];
	return top ? top.role : { jobRoleId: null, jobRoleName: null };
}
